import { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { setIsSending } from '../Store/features/chat/chatSlice';
import toast from 'react-hot-toast';
import { setIsDragActive, setFileDlpModalState } from '../Store/features/environment/environmentSlice';
import { chatService } from '../Services/chatService';
import { DLPState } from '../Types/Enums';
import { isFileTypeAllowed } from '../utils/fileValidation';

const useDragAndDrop = ({
    fileTypes,
    dispatch,
    chatPrompt,
    selectedPromptIdentifier,
    setTmpFileNames,
    setFiles,
    i18n,
    combinedDlp,
    allowedMimeTypes,
}: {
    fileTypes: { [key: string]: string[] };
    dispatch: Function;
    chatPrompt: any;
    selectedPromptIdentifier: any;
    setTmpFileNames: Function;
    setFiles: Function;
    i18n: any;
    combinedDlp: DLPState | null;
    allowedMimeTypes?: string[];
}) => {
    const onDrop = useCallback(
        async (acceptedFiles: any) => {
            if (!acceptedFiles || acceptedFiles.length === 0) {
                toast.error(
                    i18n.translate({
                        id: 'fileUpload.error.upload.general',
                        sourceName: 'FileUpload',
                    }),
                );
                dispatch(setIsDragActive(false));
                return;
            }

            let allFiles: any[] = [];
            const uploadPromises = acceptedFiles.map((file: any) => {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = async () => {
                        const arrayBuffer = reader.result;
                        if (arrayBuffer) {
                            dispatch(setIsSending(true));

                            const maxFileSize = 64 * 1024 * 1024;
                            if (file.size > maxFileSize) {
                                toast.error(
                                    i18n.translate({
                                        id: 'fileUpload.error.fileSize',
                                        sourceName: 'FileUpload',
                                        params: {
                                            fileName: file.name,
                                        },
                                    }),
                                );
                                dispatch(setIsSending(false));
                                reject(new Error('File too large'));
                                return;
                            }

                            if (!isFileTypeAllowed(file, fileTypes, allowedMimeTypes)) {
                                toast.error(
                                    i18n.translate({
                                        id: 'fileUpload.error.fileType',
                                        sourceName: 'FileUpload',
                                        params: {
                                            fileName: file.name,
                                        },
                                    }),
                                );
                                dispatch(setIsSending(false));
                                reject(new Error('File type not allowed'));
                                return;
                            }

                            try {
                                const formData = new FormData();
                                const fileForFormData = new File([file], file.name, { type: file.type });
                                formData.append('file', fileForFormData);
                                formData.append(
                                    'promptIdentifier',
                                    chatPrompt ? chatPrompt.identifier : selectedPromptIdentifier,
                                );

                                const response = await chatService.uploadFile(formData);
                                if (response.data.error) {
                                    toast.error(response.data.error);
                                    dispatch(setIsSending(false));
                                    reject(new Error(response.data.error));
                                    return;
                                }

                                allFiles = [...allFiles, response.data];
                                resolve(response.data);
                            } catch (error) {
                                toast.error(
                                    i18n.translate({
                                        id: 'fileUpload.error.upload',
                                        sourceName: 'FileUpload',
                                        params: {
                                            fileName: file.name,
                                        },
                                    }),
                                );
                                console.error('File upload error:', error);
                                reject(error);
                            } finally {
                                dispatch(setIsSending(false));
                            }
                        }
                    };
                    reader.onerror = () => reject(new Error('FileReader error'));
                    reader.readAsArrayBuffer(file);
                });
            });

            try {
                await Promise.all(uploadPromises);

                if (combinedDlp === DLPState.dlp_optional || combinedDlp === DLPState.dlp_on) {
                    dispatch(
                        setFileDlpModalState({
                            state: 'open',
                            files: allFiles,
                            combinedDlp: combinedDlp ?? DLPState.dlp_on,
                            callback: (files: any[]) => {
                                setFiles((prevFiles: any) => [...prevFiles, ...files]);
                            },
                        }),
                    );
                } else {
                    setFiles((prevFiles: any) => [...prevFiles, ...allFiles]);
                }
            } catch (error) {
                console.error('Error during file uploads:', error);
            }

            dispatch(setIsDragActive(false));
        },
        [dispatch, chatPrompt, selectedPromptIdentifier, setFiles, i18n, combinedDlp, fileTypes, allowedMimeTypes],
    );

    const dropzoneConfig = useMemo(
        () => ({
            accept: fileTypes,
            noClick: true,
            noKeyboard: true,
            onDrop,
            onDragEnter: () => dispatch(setIsDragActive(true)),
            onDragLeave: () => dispatch(setIsDragActive(false)),
        }),
        [fileTypes, onDrop, dispatch],
    );

    const { getRootProps, getInputProps } = useDropzone(dropzoneConfig);

    return { getRootProps, getInputProps };
};
export default useDragAndDrop;
